/**
 * Phase PWA — Inspection queue badge.
 *
 * Renders a tiny pill in the manager portal header showing how many
 * inspections are queued offline. Click to open a dropdown showing
 * per-row state with a manual "Retry now" trigger. Rows that hit a 4xx
 * (validation failure) are shown with their error so the admin can act.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CloudOff, RefreshCw, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { listQueue, deleteRow, updateRow } from "@/pwa/inspection-queue";
import { flushInspectionQueue, onQueueChange } from "@/pwa/sync-engine";

export default function InspectionQueueBadge() {
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const containerRef = useRef(null);

    const refresh = useCallback(async () => {
        try { setRows(await listQueue()); }
        catch { setRows([]); }
    }, []);

    useEffect(() => {
        refresh();
        const unsub = onQueueChange(refresh);
        const t = setInterval(refresh, 5_000);
        return () => { unsub(); clearInterval(t); };
    }, [refresh]);

    useEffect(() => {
        const onDoc = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        if (open) document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    const pending = rows.filter((r) => !r.last_error && r.step !== "done").length;
    const failed = rows.filter((r) => Boolean(r.last_error)).length;
    if (pending === 0 && failed === 0) return null;

    const onRetry = async () => {
        setRetrying(true);
        try {
            await flushInspectionQueue();
            await refresh();
        } finally { setRetrying(false); }
    };
    const onClearFailed = async (id) => {
        await deleteRow(id);
        refresh();
    };
    const onResetError = async (row) => {
        row.last_error = null;
        await updateRow(row);
        refresh();
    };

    return (
        <div ref={containerRef} className="relative">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                data-testid="pwa-queue-badge"
                className={
                    "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset transition-colors " +
                    (failed > 0
                        ? "bg-rose-50 text-rose-900 ring-rose-200 hover:bg-rose-100"
                        : "bg-amber-50 text-amber-900 ring-amber-200 hover:bg-amber-100")
                }
            >
                <CloudOff size={12} aria-hidden="true" />
                <span data-testid="pwa-queue-count">{pending + failed}</span>
                <span className="hidden sm:inline">
                    inspection{(pending + failed) === 1 ? "" : "s"} queued
                </span>
            </button>
            {open && (
                <div
                    data-testid="pwa-queue-dropdown"
                    className="absolute right-0 mt-2 w-80 rounded-lg bg-white ring-1 ring-slate-200 shadow-lg p-3 z-50"
                >
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold text-slate-900">Offline inspections</p>
                        <button
                            type="button"
                            onClick={onRetry}
                            disabled={retrying}
                            data-testid="pwa-queue-retry"
                            className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900 disabled:opacity-50"
                        >
                            {retrying
                                ? <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                                : <RefreshCw size={12} aria-hidden="true" />}
                            Retry now
                        </button>
                    </div>
                    <ul className="space-y-2 max-h-80 overflow-y-auto">
                        {rows.map((r) => (
                            <li
                                key={r.id}
                                data-testid={`pwa-queue-row-${r.id}`}
                                className={
                                    "rounded-md p-2 text-xs " +
                                    (r.last_error
                                        ? "bg-rose-50 ring-1 ring-inset ring-rose-200"
                                        : "bg-slate-50 ring-1 ring-inset ring-slate-200")
                                }
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className="font-mono text-[10px] text-slate-500 truncate">
                                        rental {r.rental_id?.slice(0, 8)}…
                                    </span>
                                    <span className="text-[10px] text-slate-600">
                                        {r.step === "done"
                                            ? <CheckCircle2 size={11} className="inline text-emerald-600" />
                                            : <span>{r.step}</span>}
                                    </span>
                                </div>
                                {r.last_error && (
                                    <p className="mt-1 text-rose-800 leading-snug flex items-start gap-1">
                                        <AlertCircle size={11} className="mt-0.5 shrink-0" />
                                        <span>{r.last_error}</span>
                                    </p>
                                )}
                                <div className="flex gap-2 mt-1">
                                    {r.last_error && (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => onResetError(r)}
                                                className="text-[10px] text-blue-700 hover:underline"
                                                data-testid={`pwa-queue-row-retry-${r.id}`}
                                            >Retry</button>
                                            <button
                                                type="button"
                                                onClick={() => onClearFailed(r.id)}
                                                className="text-[10px] text-rose-700 hover:underline"
                                                data-testid={`pwa-queue-row-clear-${r.id}`}
                                            >Discard</button>
                                        </>
                                    )}
                                    <span className="ml-auto text-[10px] text-slate-500">
                                        attempts: {r.attempts || 0}
                                    </span>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
